import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import type { ContentFileDto } from "shared";
import { useDebounceValue } from "usehooks-ts";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useDialog } from "../common/dialog/Dialog";
import { useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Spinner } from "../common/Spinner";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Skeleton } from "../components/Skeleton";
import { Tabs } from "../components/Tabs";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { MediaUploadDialog, onDrop } from "../media/MediaUploadDialog";
import { ModalImportFromCanva } from "../media/ModalImportFromCanva";
import { ModalImportFromHeyGen } from "../media/ModalImportFromHeyGen";
import "../media/ModalImportFromCanva.css";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { markCanvaEditorOpened } from "../media/canvaEditorSession";
import {
	useSetUploadFolder,
	useUploadComplete,
	useUploadList,
} from "../media/upload";
import { useCanvaQuery, useHeyGenQuery } from "../settings/useIntegrations";
import { ModalMakeVideo } from "../video/ModalMakeVideo";
import {
	useVideoAvailability,
	useVideoReadyWatcher,
} from "../video/useVideoGenerations";
import DropZoneWrapper from "./DropZoneWrapper";
import { FolderSidebar } from "./FolderSidebar";
import { ModalGenerateCarousel } from "./generate/ModalGenerateCarousel";
import { ModalGenerateImagesV2 } from "./generate/ModalGenerateImagesV2";
import { useMediaActions } from "./useMediaActions";
import { useMediaPage } from "./useMediaPage";

const MEDIA_SKELETON_KEYS = [
	"m0",
	"m1",
	"m2",
	"m3",
	"m4",
	"m5",
	"m6",
	"m7",
	"m8",
	"m9",
	"m10",
	"m11",
] as const;

function MediaLibrarySkeleton() {
	return (
		// biome-ignore lint/a11y/useSemanticElements: presentational loading placeholder
		<div
			className="content-container"
			role="status"
			aria-busy="true"
			aria-label="Loading media"
		>
			{MEDIA_SKELETON_KEYS.map((key) => (
				<Skeleton key={key} width="100%" height="100%" />
			))}
		</div>
	);
}

function MediaThumbnail({ file }: { file: ContentFileDto }) {
	const [loading, setLoading] = useState(true);
	const cacheKey = file.importedAt ?? file.updatedAt;
	const thumbnail = file.thumbnails["256"] ?? file.url;
	const thumbnailSrc = `${thumbnail}?v=${encodeURIComponent(cacheKey)}`;
	const videoSrc = `${file.url}?v=${encodeURIComponent(cacheKey)}`;

	return (
		<NavLink to={LinkGen.MediaEdit(file.id)}>
			<div className="media-thumb-wrapper">
				{file.source === "canva" && (
					<span className="canva-source-badge">From Canva</span>
				)}
				{file.source === "heygen" && (
					<span className="canva-source-badge">From HeyGen</span>
				)}
				{file.source === "heygen_generated" && (
					<span className="canva-source-badge">Made with Moxie</span>
				)}
				{loading && (
					<div className="media-thumb-loading">
						<Spinner />
					</div>
				)}
				{file.source === "canva" && (
					<span
						className="badge bg-light text-dark position-absolute"
						style={{ bottom: 6, left: 6, zIndex: 1 }}
					>
						From Canva
					</span>
				)}
				{file.type === "image" && (
					<img
						key={cacheKey}
						src={thumbnailSrc}
						alt={file.filename}
						onLoad={() => setLoading(false)}
						onError={() => setLoading(false)}
					/>
				)}
				{file.type === "video" && (
					<video
						key={cacheKey}
						tabIndex={-1}
						muted
						playsInline
						preload="auto"
						onLoadedData={() => setLoading(false)}
						onError={() => setLoading(false)}
					>
						<source src={videoSrc} type="video/mp4" />
					</video>
				)}
			</div>
		</NavLink>
	);
}

function PaginatedContent({
	folderId,
	query,
	sourceFilter,
}: {
	folderId?: number | null;
	query: string;
	sourceFilter?: string;
}) {
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
		useMediaPage({ folderId, query: query || undefined, source: sourceFilter });

	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const allFiles = useMemo(
		() => data?.pages.flatMap((p) => p.files) ?? [],
		[data],
	);

	const totalCount = data?.pages[0]?.totalCount ?? 0;

	const images = useMemo(
		() => allFiles.filter((f) => f.type === "image"),
		[allFiles],
	);
	const videos = useMemo(
		() => allFiles.filter((f) => f.type === "video"),
		[allFiles],
	);

	if (isLoading) {
		return <MediaLibrarySkeleton />;
	}

	return (
		<>
			<Tabs id="media-tabs" className="tabs-content">
				<Tabs.Panel eventKey="All" title={`All (${totalCount})`}>
					<div className="content-container">
						{allFiles.map((file) => (
							<MediaThumbnail key={file.id} file={file} />
						))}
					</div>
				</Tabs.Panel>
				<Tabs.Panel eventKey="images" title={`Images (${images.length})`}>
					<div className="content-container">
						{images.map((file) => (
							<MediaThumbnail key={file.id} file={file} />
						))}
					</div>
				</Tabs.Panel>
				<Tabs.Panel eventKey="videos" title={`Videos (${videos.length})`}>
					<div className="content-container">
						{videos.map((file) => (
							<MediaThumbnail key={file.id} file={file} />
						))}
					</div>
				</Tabs.Panel>
			</Tabs>

			<div ref={sentinelRef} style={{ height: 1 }} />

			{isFetchingNextPage && (
				<div className="d-flex justify-content-center p-3">
					<Spinner />
				</div>
			)}

			{allFiles.length === 0 && !isLoading && (
				<p className="text-center text-muted py-4">No files found.</p>
			)}
		</>
	);
}

export const PageMediaLibrary = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const emit = useAlertEmit();
	const [searchWord, setSearchWord] = useState("");
	const [debouncedQuery] = useDebounceValue(searchWord, 500);
	const [sourceFilter, setSourceFilter] = useState<string | undefined>(
		undefined,
	);
	const isCanvaEnabled = useFeatureFlag("CanvaIntegration");
	const { data: canvaStatus } = useCanvaQuery();
	const isHeyGenEnabled = useFeatureFlag("HeyGenIntegration");
	const { data: heygenStatus } = useHeyGenQuery();
	const videoAvailability = useVideoAvailability();
	const [selectedFolderId, setSelectedFolderId] = useState<
		number | null | undefined
	>(undefined);
	const { invalidateMedia } = useMediaActions();
	useVideoReadyWatcher(videoAvailability.flagOn, () => invalidateMedia());
	const { addFile, addError } = useUploadList();
	const setUploadFolder = useSetUploadFolder();
	useUploadComplete(() => invalidateMedia());
	const { open } = useModal();
	const dialog = useDialog();

	// Sync selected folder to upload store
	useEffect(() => {
		setUploadFolder(
			typeof selectedFolderId === "number" ? selectedFolderId : null,
		);
	}, [selectedFolderId, setUploadFolder]);

	const dropzone = useDropzone({
		onDrop: (files) => {
			dialog.open(MediaUploadDialog, {});
			onDrop(addFile, addError)(files);
		},
		noClick: true,
		noKeyboard: true,
	});

	const handleUploadButton = () => {
		dialog.open(MediaUploadDialog, {});
	};

	const handleImportFromCanva = () => {
		dialog.open(ModalImportFromCanva, {
			folderId: typeof selectedFolderId === "number" ? selectedFolderId : null,
		});
	};

	const handleImportFromHeyGen = () => {
		dialog.open(ModalImportFromHeyGen, {
			folderId: typeof selectedFolderId === "number" ? selectedFolderId : null,
		});
	};

	const handleCreateInCanva = async () => {
		try {
			const folderId =
				typeof selectedFolderId === "number" ? selectedFolderId : null;
			const res = await Api.CanvaCreateDesign({
				title: "Moxie design",
				width: 1080,
				height: 1080,
				folderId,
			});
			markCanvaEditorOpened();
			window.open(res.data.editUrl, "_blank", "noopener,noreferrer");
		} catch {
			emit({
				type: AlertType.ERROR,
				content:
					"Could not create a Canva design. Try reconnecting in Settings → Apps.",
			});
		}
	};

	useEffect(() => {
		const message = searchParams.get("message");
		if (!message) {
			return;
		}
		emit({ type: AlertType.SUCCESS, content: message });
		const next = new URLSearchParams(searchParams);
		next.delete("message");
		setSearchParams(next, { replace: true });
	}, [emit, searchParams, setSearchParams]);

	const handleOpenImageGen = useCallback(() => {
		open(ModalGenerateImagesV2, {});
	}, [open]);

	const handleOpenCarouselGen = useCallback(() => {
		open(ModalGenerateCarousel, {});
	}, [open]);

	// Get counts for sidebar from current page data
	const { data: pageData } = useMediaPage({
		folderId: undefined,
		query: undefined,
	});
	const totalCount = pageData?.pages[0]?.totalCount ?? 0;

	const { data: unfiledData } = useMediaPage({
		folderId: null,
		query: undefined,
	});
	const unfiledCount = unfiledData?.pages[0]?.totalCount ?? 0;

	return (
		<AppPage
			headerActions={
				<div className="d-flex gap-2">
					<Button onClick={handleOpenCarouselGen} size="sm">
						Generate Carousel
					</Button>
					<Button onClick={handleOpenImageGen} size="sm">
						Generate Images
					</Button>
				</div>
			}
			title="Media library"
			subtitle="Upload, organize, and reuse images and videos for your posts."
		>
			<div className="d-flex align-items-end pb-4 gap-4">
				<ExtraForm.Group className="m-0 flex-grow-1" label="">
					<ExtraForm.Control
						onChange={(e) => setSearchWord(e.target.value.toLowerCase().trim())}
						placeholder="Search files by name or description"
						type="text"
					/>
				</ExtraForm.Group>

				<Button onClick={handleUploadButton}>Upload</Button>
				{isCanvaEnabled &&
					canvaStatus?.connected &&
					!canvaStatus.tokenExpired && (
						<>
							<Button variant="secondary" onClick={handleImportFromCanva}>
								Import from Canva
							</Button>
							<Button variant="secondary" onClick={handleCreateInCanva}>
								Create in Canva
							</Button>
						</>
					)}
				{isCanvaEnabled && canvaStatus?.tokenExpired && (
					<Button
						variant="secondary"
						onClick={() => navigate(LinkGen.Settings.Apps())}
					>
						Reconnect Canva
					</Button>
				)}
				{isHeyGenEnabled &&
					(heygenStatus?.connected ? (
						heygenStatus.state === "invalid" ? (
							<Button
								variant="secondary"
								onClick={() => navigate(LinkGen.Settings.Apps())}
							>
								Reconnect HeyGen
							</Button>
						) : (
							<Button variant="secondary" onClick={handleImportFromHeyGen}>
								Import from HeyGen
							</Button>
						)
					) : (
						<Button
							variant="secondary"
							onClick={() => navigate(LinkGen.Settings.Apps())}
						>
							Connect HeyGen
						</Button>
					))}
				{videoAvailability.enabled && (
					<Button
						variant="secondary"
						icon="movie"
						onClick={() => open(ModalMakeVideo, {})}
					>
						Create video
					</Button>
				)}
			</div>

			<div className="d-flex gap-2 mb-3">
				<Button
					size="sm"
					variant={sourceFilter === undefined ? "primary" : "secondary"}
					onClick={() => setSourceFilter(undefined)}
				>
					All sources
				</Button>
				<Button
					size="sm"
					variant={sourceFilter === "canva" ? "primary" : "secondary"}
					onClick={() => setSourceFilter("canva")}
				>
					From Canva
				</Button>
				{isHeyGenEnabled && (
					<Button
						size="sm"
						variant={sourceFilter === "heygen" ? "primary" : "secondary"}
						onClick={() => setSourceFilter("heygen")}
					>
						From HeyGen
					</Button>
				)}
			</div>

			<div className="media-layout">
				<FolderSidebar
					selectedFolderId={selectedFolderId}
					onSelect={setSelectedFolderId}
					totalCount={totalCount}
					unfiledCount={unfiledCount}
				/>

				<DropZoneWrapper dropzone={dropzone}>
					<Card>
						<Card.Body>
							<PaginatedContent
								folderId={selectedFolderId}
								query={debouncedQuery}
								sourceFilter={sourceFilter}
							/>
						</Card.Body>
					</Card>
				</DropZoneWrapper>
			</div>
		</AppPage>
	);
};
